import { Box, Typography, Divider, Link } from '@mui/material';
import LegalPageLayout from '../../components/legal/LegalPageLayout';

const NAVY = '#0a326d';
const TEXT = '#334155';

function Section({ number, title, children }) {
  return (
    <Box sx={{ mt: 3 }}>
      <Typography variant="subtitle1" fontWeight={700} color={NAVY} sx={{ mb: 1 }}>
        {number}. {title}
      </Typography>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>{children}</Box>
    </Box>
  );
}

function P({ children }) {
  return (
    <Typography variant="body2" sx={{ color: TEXT, lineHeight: 1.7, textAlign: 'justify' }}>
      {children}
    </Typography>
  );
}

function Bullets({ items }) {
  return (
    <Box component="ul" sx={{ m: 0, pl: 3, display: 'flex', flexDirection: 'column', gap: 1 }}>
      {items.map((item, i) => (
        <Typography key={i} component="li" variant="body2" sx={{ color: TEXT, lineHeight: 1.7, textAlign: 'justify' }}>
          {item}
        </Typography>
      ))}
    </Box>
  );
}

export default function TermsOfService() {
  return (
    <LegalPageLayout>
      <Box sx={{ textAlign: 'center' }}>
        <Typography
          variant="h5"
          fontWeight={800}
          color="#072146"
          sx={{ letterSpacing: 0.3, fontSize: { xs: '1.1rem', sm: '1.3rem', md: '1.4rem' } }}
        >
          TIFOXR END USER LICENSE AGREEMENT – Product Terms
        </Typography>
        <Typography variant="body2" color="#64748b" sx={{ mt: 0.5 }}>
          Last Updated: 21th August, 2026
        </Typography>
      </Box>

      <Divider sx={{ my: 1 }} />

      <Section number={1} title="Acceptance of Product Terms">
        <P>
          These TifoXR Product Terms and End User License Agreement ("Terms") govern access to and use of TifoXR
          Immersive, TifoXR 3D Streaming, TifoXR CMS, and related websites, software, hosted services, content,
          features, analytics, and support services (collectively, the "TifoXR Services").
        </P>
        <P>
          By creating an account, clicking "I Agree," accessing the TifoXR Services, or using the TifoXR Services,
          you agree to these Terms. Where applicable, acceptance may be recorded electronically.
        </P>
        <P>
          If you use the TifoXR Services on behalf of a club, employer, organization, or other entity, you
          represent that you have authority to bind that entity and its users.
        </P>
        <P>
          If a club-specific version of the TifoXR Services includes additional terms, privacy notices,
          data-processing terms, payment terms, fan-code requirements, or other applicable policies, those
          club-specific terms apply to that deployment. If there is a conflict, the applicable club-specific terms
          control only for that club deployment unless they expressly state otherwise.
        </P>
        <P>
          Certain TifoXR deployments may also be subject to GMS's DSA Information, Content Moderation, and Abuse
          Reporting framework and other applicable legal requirements.
        </P>
      </Section>

      <Section number={2} title="Eligibility and Age Requirements">
        <P>
          You may use the TifoXR Services only if you can form a binding contract under applicable law and are not
          barred from using the services by applicable law.
        </P>
        <P>
          The minimum age is thirteen (13). Users under 13 are not permitted to access or use TifoXR unless a
          particular deployment expressly permits such access in compliance with applicable law and required
          parental or guardian consent.
        </P>
        <P>
          GMS does not knowingly collect personal information from children under 13 without legally required
          consent. If the TifoXR Services are made available to children or youth fan audiences in a particular
          deployment, GMS may require age screening, parental consent, limited-data settings, or other safeguards.
        </P>
      </Section>

      <Section number={3} title="Accounts and Credentials">
        <P>
          You are responsible for maintaining the confidentiality of your account credentials and for activity
          under your account. You agree to provide accurate information, keep your information current, and notify
          GMS promptly of unauthorized access or security concerns.
        </P>
        <P>
          Club administrators and CMS users are responsible for activity conducted through their credentials and
          for ensuring that access is granted only to authorized personnel.
        </P>
        <P>
          Authentication may be provided through Microsoft Identity Platform or other third-party identity
          providers used in connection with a club-specific deployment. GMS does not store end-user passwords
          where authentication is handled by those providers.
        </P>
      </Section>

      <Section number={4} title="Limited License">
        <P>
          Subject to these Terms, GMS grants you a limited, personal, non-exclusive, non-transferable,
          non-sublicensable, revocable license to access and use the TifoXR Services for their intended purposes
          during the period in which you are authorized to use them.
        </P>
        <P>You may not:</P>
        <Bullets
          items={[
            'copy, modify, reverse engineer, decompile, disassemble, or create derivative works from the TifoXR Services;',
            'sell, resell, sublicense, rent, lease, distribute, or commercially exploit the TifoXR Services except as expressly authorized;',
            'scrape, harvest, or extract data or content;',
            'bypass access controls, rate limits, security controls, or usage restrictions;',
            'use the TifoXR Services to build a competing product or service;',
            "interfere with platform operations or other users' experience;",
            'remove proprietary notices; or',
            'use the TifoXR Services in violation of law, club rules, league rules, these Terms, or applicable third-party terms.',
          ]}
        />
      </Section>

      <Section number={5} title="Fan-Facing Experiences">
        <P>
          TifoXR Immersive and TifoXR 3D Streaming may provide fan engagement, digital content, streaming,
          interactive, or immersive experiences.
        </P>
        <P>
          Available features may vary by club, territory, device, browser, bandwidth, event, subscription, rights
          restrictions, and operational requirements.
        </P>
        <P>GMS may modify, suspend, or discontinue features as permitted by law and applicable agreements.</P>
      </Section>

      <Section number={6} title="QPR Club-Specific Terms">
        <P>Club: Queens Park Rangers FC</P>
        <P>
          Privacy Notice:{' '}
          <Link href="https://www.qpr.co.uk/privacy-notice" target="_blank" rel="noopener noreferrer" sx={{ fontWeight: 700 }}>
            https://www.qpr.co.uk/privacy-notice
          </Link>
        </P>
        <P>
          For fan personal data processed in the QPR environment, Queens Park Rangers FC is Controller. Global
          Mobile Software LLC is a Processor for hosting and delivery. For analytics metrics exposed via Microsoft
          Fabric/Power BI, Global Mobile Software LLC is Controller for aggregated metrics.
        </P>
        <P>
          Global Mobile Software LLC has appointed Data Protection Representative Limited (trading as DataRep) as
          its EU Representative at 77 Camden Street Lower, Dublin D02 XE80, Ireland and its UK Representative at
          107-111 Fleet Street, London EC4A 2AB, UK.
        </P>
        <P>
          DataRep contact:{' '}
          <Link href="mailto:tifoxr@datarep.com" sx={{ fontWeight: 700 }}>
            tifoxr@datarep.com
          </Link>
        </P>
        <P>
          DataRep website:{' '}
          <Link href="https://www.datarep.com/tifoxr" target="_blank" rel="noopener noreferrer" sx={{ fontWeight: 700 }}>
            https://www.datarep.com/tifoxr
          </Link>
        </P>
        <P>Minimum Age: 13 years.</P>
        <P>
          TifoXR is intended for users aged 13 and above. Users under 13 are not permitted to access or use the
          service. If GMS becomes aware that personal information has been collected from a user under 13, it will
          take appropriate steps to delete the information in accordance with applicable law.
        </P>
      </Section>

      <Section number={7} title="TifoXR CMS">
        <P>
          TifoXR CMS may be used by authorized club personnel and contractors to manage content, publishing
          workflows, metadata, and related configurations.
        </P>
        <P>
          CMS users must ensure that uploaded or submitted materials are accurate, lawful, properly licensed, and
          consistent with applicable club, league, sponsor, player, and third-party rights.
        </P>
        <P>
          CMS access is limited to authorized GMS personnel, club personnel, and other approved users with
          role-based permissions.
        </P>
        <P>
          Certain content workflows may require proposal, approval, or rejection steps before content is deployed.
        </P>
      </Section>

      <Section number={8} title="User Content and Club Content">
        <P>
          Depending on the deployment and enabled features, TifoXR may support user-generated content and
          interactions, including text chat, live voice or audio, usernames, avatars, reactions, and friend
          requests.
        </P>
        <P>
          Authorized club personnel, CMS users, administrators, and other users may also submit content where a
          specific content-submission feature is enabled.
        </P>
        <P>
          If you upload, submit, post, transmit, or otherwise provide content through the TifoXR Services ("User
          Content"), you retain any rights you own in that content.
        </P>
        <P>
          You grant GMS a worldwide, non-exclusive, royalty-free license to host, store, reproduce, display,
          transmit, format, adapt for technical purposes, process, and otherwise use User Content as necessary to
          operate, provide, secure, improve, and support the TifoXR Services, comply with law, and enforce these
          Terms.
        </P>
        <P>
          You represent and warrant that you have all rights, permissions, consents, and lawful bases necessary to
          provide User Content and to grant the above license.
        </P>
        <P>
          You may not submit content that is unlawful, infringing, defamatory, harassing, obscene, deceptive,
          harmful, or otherwise violates these Terms or applicable law.
        </P>
      </Section>

      <Section number={9} title="Content Moderation, Reporting, and Appeals">
        <P>
          TifoXR content and user interactions may be subject to moderation in accordance with these Terms, the
          Acceptable Use Policy, applicable law, and the applicable DSA Information, Content Moderation, and Abuse
          Reporting framework.
        </P>
        <P>
          GMS may use automated tools to assist with moderation. Hive.ai is currently used for text chat moderation
          only. Voice chat, where enabled, is currently not transcribed or automatically moderated by AI.
        </P>
        <P>
          GMS may remove content, issue warnings, temporarily suspend accounts, or permanently restrict access
          where permitted by applicable terms and law.
        </P>
        <P>
          Users may report potentially illegal content or other violations through the available reporting
          channels, including the in-app reporting functionality where available and the designated
          abuse-reporting contact.
        </P>
        <P>
          Where applicable, users may receive information about content restrictions and available redress or
          appeals in accordance with the applicable TifoXR policies and law.
        </P>
        <P>
          The dedicated DSA Information, Content Moderation, and Abuse Reporting page contains additional
          information concerning reporting, statements of reasons, appeals, Trusted Flaggers, and applicable
          Digital Services Act procedures.
        </P>
      </Section>

      <Section number={10} title="Club, League, Player, Sponsor, and Third-Party Rights">
        <P>
          TifoXR experiences may include club marks, league materials, sponsor materials, player images, match
          footage, music, statistics, or third-party content.
        </P>
        <P>
          Such materials may be subject to separate ownership, license, publicity, broadcast, league, territorial,
          or sponsor restrictions.
        </P>
        <P>You receive no ownership interest in any such materials.</P>
        <P>
          Clubs are responsible for ensuring they have the necessary rights, clearances, licenses, and permissions
          for club-controlled content made available through TifoXR.
        </P>
      </Section>

      <Section number={11} title="Payments, Subscriptions, and Refunds">
        <P>
          Certain TifoXR Services may require payment, subscription, ticket purchase, digital purchase, or other
          transaction.
        </P>
        <P>
          Payment terms, taxes, renewal terms, cancellation terms, and refund rights will be disclosed at checkout
          or in club-specific terms.
        </P>
        <P>
          Transactions may be processed by the applicable club partner, its designated payment processor,
          ticketing provider, app store, or another third party designated for the relevant deployment.
        </P>
        <P>
          In current club-specific deployments, users typically purchase goods, tickets, subscriptions, or
          experiences from the applicable club partner, and funds may be collected through club-controlled payment
          accounts such as PayPal or Stripe accounts.
        </P>
        <P>
          Unless otherwise required by law or stated at checkout, fees are non-refundable once access is provided
          or digital content is delivered.
        </P>
        <P>
          Refund, cancellation, chargeback, tax, and failed-payment terms may vary by club deployment and should be
          disclosed at checkout or in club-specific terms.
        </P>
      </Section>

      <Section number={12} title="Acceptable Use">
        <P>You agree not to use the TifoXR Services to:</P>
        <Bullets
          items={[
            'violate any law or third-party right;',
            'harass, threaten, abuse, defame, impersonate, or deceive others;',
            'upload malware or harmful code;',
            'interfere with security, availability, or integrity of the TifoXR Services;',
            'scrape, mine, harvest, or collect data without authorization;',
            'upload infringing, unlawful, or unauthorized content;',
            'manipulate engagement, votes, metrics, purchases, or platform analytics;',
            'circumvent territorial, payment, access, or rights-management restrictions;',
            'misuse club, league, player, sponsor, or fan data; or',
            'engage in conduct that GMS reasonably determines may harm GMS, a club partner, other users, or the TifoXR Services.',
          ]}
        />
      </Section>

      <Section number={13} title="TifoXR Health and Safety Warning">
        <P>
          TifoXR immersive, 3D, augmented, virtual, mixed-reality, spatial, or motion-based experiences may cause
          discomfort or health effects in some users, including dizziness, nausea, eye strain, disorientation, loss
          of balance, motion sickness, headaches, fatigue, photosensitive reactions, or seizures.
        </P>
        <P>Before using any immersive experience:</P>
        <Bullets
          items={[
            'use the experience only in a safe physical environment;',
            'remain aware of your surroundings;',
            'stop immediately if you feel discomfort;',
            'do not use while driving, walking in unsafe areas, operating equipment, or performing tasks requiring attention;',
            'consult a healthcare professional before use if you have a history of seizures, photosensitivity, balance disorders, heart conditions, neurological conditions, or other relevant health concerns;',
            'supervise minors; and',
            'follow all device-manufacturer safety instructions.',
          ]}
        />
        <P>
          Where implemented, users may be required to acknowledge a health and safety warning before entering an
          immersive experience.
        </P>
        <P>
          GMS does not guarantee that immersive experiences will be safe or suitable for every user, device, or
          environment.
        </P>
        <P>
          Current versions may include flashing lights, motion, simulated movement, and 3D depth effects.
          Headset-based functionality is not currently enabled, but future versions may add additional immersive
          hardware support.
        </P>
      </Section>

      <Section number={14} title="AI-Generated and Automated Features">
        <P>
          The TifoXR Services may use AI, automated, algorithmic, or machine-assisted technology for specified
          functions, including text-chat moderation, analytics, translations, summaries, or other operational
          purposes where enabled.
        </P>
        <P>Hive.ai is currently used for text-chat moderation only.</P>
        <P>
          TifoXR does not currently use personalized algorithmic ranking or recommendation of user-generated
          content. Chat is displayed chronologically, with newer messages appearing at the bottom.
        </P>
        <P>Voice chat, where enabled, is currently not transcribed or automatically moderated by AI.</P>
        <P>
          Where AI-assisted processing is used, outputs may be incomplete, inaccurate, synthetic, outdated, biased,
          or unsuitable for a particular purpose. AI outputs should not be treated as legal, medical, financial,
          professional, or safety advice.
        </P>
      </Section>

      <Section number={15} title="Advertising and Sponsored Content">
        <P>
          Depending on the applicable deployment, TifoXR may display advertising or sponsored content, including
          images or videos managed through the TifoXR CMS.
        </P>
        <P>
          Advertising may be shown consistently to users within the applicable space and is not targeted based on
          user data or user profiling.
        </P>
        <P>
          GMS does not use sensitive personal data for advertising purposes and does not use targeted advertising
          to minors.
        </P>
        <P>Users may report advertising or sponsored content through the applicable reporting channels.</P>
      </Section>

      <Section number={16} title="Analytics and Aggregated Data">
        <P>
          GMS may collect usage, performance, engagement, transaction, and technical data relating to the TifoXR
          Services.
        </P>
        <P>
          Subject to applicable law and privacy notices, GMS may use aggregated or de-identified data to operate,
          secure, analyze, improve, and promote the TifoXR Services.
        </P>
      </Section>

      <Section number={17} title="Privacy">
        <P>
          The collection and use of personal information are described in the applicable TifoXR Privacy Policy,
          in-app notice, Cookie Policy, and any club-specific privacy notice.
        </P>
        <P>
          Depending on the deployment, GMS may act as an independent controller, joint controller, processor,
          service provider, or vendor for certain data processing activities.
        </P>
        <P>
          In certain club-specific deployments, the relevant club partner may serve as the local contact for
          privacy-related matters in its territory.
        </P>
      </Section>

      <Section number={18} title="Suspension and Termination">
        <P>
          GMS may suspend or terminate access to the TifoXR Services if GMS reasonably believes that you have
          violated these Terms, created security or legal risk, infringed rights, misused the platform, failed to
          pay required fees, or used the services in a way that may harm GMS, a club partner, other users, or the
          platform.
        </P>
        <P>
          Any content restriction or account action that is subject to applicable DSA requirements will be handled
          in accordance with those requirements and the applicable TifoXR policies.
        </P>
      </Section>

      <Section number={19} title="Disclaimers">
        <P>The TifoXR Services are provided "as is" and "as available."</P>
        <P>
          To the maximum extent permitted by law, GMS disclaims all warranties, express or implied, including
          warranties of merchantability, fitness for a particular purpose, title, non-infringement, uninterrupted
          availability, error-free operation, accuracy, and compatibility with any device, browser, network, or
          third-party service.
        </P>
        <P>
          Nothing in these Terms excludes or limits any warranty, right, or remedy that cannot lawfully be excluded
          or limited under applicable law.
        </P>
      </Section>

      <Section number={20} title="Limitation of Liability">
        <P>
          To the maximum extent permitted by law, GMS and its officers, members, managers, employees, contractors,
          agents, affiliates, licensors, and service providers will not be liable for indirect, incidental,
          consequential, special, exemplary, punitive, lost-profit, lost-data, business-interruption, or
          reputational damages arising out of or relating to the TifoXR Services.
        </P>
        <P>
          To the maximum extent permitted by law, GMS's aggregate liability for claims arising out of or relating
          to the TifoXR Services will not exceed the greater of:
        </P>
        <Bullets
          items={[
            '(a) the amount you paid directly to GMS for the TifoXR Services giving rise to the claim during the three months before the event giving rise to liability; or',
            '(b) USD $100.',
          ]}
        />
        <P>
          Some jurisdictions do not allow certain warranty disclaimers or liability limitations, so some terms may
          not apply to you.
        </P>
        <P>Nothing in these Terms limits liability to the extent such limitation is prohibited by applicable law.</P>
      </Section>

      <Section number={21} title="Indemnity">
        <P>
          If you are using the TifoXR Services on behalf of a club, business, organization, or other entity, you
          agree to defend, indemnify, and hold harmless GMS and its officers, members, managers, employees,
          contractors, agents, affiliates, licensors, and service providers from and against claims, damages,
          losses, liabilities, costs, and expenses, including reasonable attorneys' fees, arising out of or
          relating to your User Content, your use of the TifoXR Services, your breach of these Terms, your
          violation of law, or your infringement of third-party rights.
        </P>
        <P>This indemnity applies to the extent permitted by applicable law.</P>
      </Section>

      <Section number={22} title="Changes to Services or Terms">
        <P>GMS may modify the TifoXR Services and these Terms from time to time.</P>
        <P>
          If changes are material, GMS will provide notice as required by law or through reasonable means such as
          posting updated terms, in-app notice, email, or account notice.
        </P>
        <P>
          Continued use after changes become effective constitutes acceptance of the updated Terms to the extent
          permitted by applicable law.
        </P>
      </Section>

      <Section number={23} title="Governing Law and Disputes">
        <P>
          These Terms are governed by the laws of the State of New York, without regard to conflict-of-law rules,
          subject to mandatory consumer-protection laws and other applicable laws that may apply in your
          jurisdiction.
        </P>
        <P>
          Any dispute arising out of or relating to these Terms or the TifoXR Services will be brought in the state
          or federal courts located in New York County, New York, unless mandatory law requires another forum.
        </P>
        <P>
          Nothing in this section limits any mandatory dispute-resolution, redress, or other rights available under
          applicable law.
        </P>
      </Section>

      <Section number={24} title="Contact">
        <P>Questions about these Terms may be sent to:</P>
        <Typography variant="body2">
          <Link href="mailto:compliance@globalmobisoft.com" sx={{ fontWeight: 700 }}>
            compliance@globalmobisoft.com
          </Link>
        </Typography>
      </Section>
    </LegalPageLayout>
  );
}
